/**
 * Unified Distribution Strategy
 *
 * Single strategy that handles all bond types (targeted and unlimited).
 * Uses intelligent ratio calculation based on bond types:
 * - All unlimited: Revenue capacity ratios
 * - All targeted: Year-percentage allocation
 * - Mixed: Year-percentage for targeted, remainder for unlimited
 *
 * This ensures targeted bonds achieve their targets in mixed scenarios.
 */

import type { RevenueMap } from '~/types';
import { roundPrincipalDown } from '../../../calculators/core/financial-math/rounding';
import { CapacityCalculator } from '../../../calculators/distribution';
import { SmartRatioCalculator } from '../../../calculators/distribution/smart-ratio-calculator';
import { DISTRIBUTION_CONFIG } from '../config';
import type { DistributionStrategy, RevenueDistributionHelperMap } from '../types';

export class UnifiedDistributionStrategy implements DistributionStrategy {
  distribute(
    helperMap: RevenueDistributionHelperMap,
    revenueMap: RevenueMap,
    currentTaxYear: number,
    revenueAvailableForDistribution: number
  ): void {
    const activeBonds = Object.entries(helperMap).filter(([_, entry]) => entry.isActive);

    if (
      activeBonds.length === 0 ||
      revenueAvailableForDistribution < DISTRIBUTION_CONFIG.MINIMUM_DISTRIBUTION_AMOUNT
    ) {
      return;
    }

    // Always use smart ratio calculation
    this.distributeWithSmartRatios(
      helperMap,
      revenueMap,
      currentTaxYear,
      revenueAvailableForDistribution
    );
  }

  /**
   * Distribution logic using smart ratio calculation
   */
  private distributeWithSmartRatios(
    helperMap: RevenueDistributionHelperMap,
    revenueMap: RevenueMap,
    currentTaxYear: number,
    revenueAvailableForDistribution: number
  ): void {
    // Step 1: Calculate minimum revenue capacities (needed for unlimited bond ratios)
    const capacities = CapacityCalculator.findMinimumRevenueCapacities(
      helperMap,
      revenueMap,
      currentTaxYear
    );
    CapacityCalculator.applyMinimumCapacities(helperMap, capacities);

    // Step 2: Calculate smart ratios based on bond types
    const ratios = SmartRatioCalculator.calculateDistributionRatios(
      helperMap,
      revenueMap,
      currentTaxYear,
      revenueAvailableForDistribution
    );

    // Step 3: Apply ratios to distribute revenue
    if (ratios.isAbsoluteAmount) {
      // Ratios are already amounts - apply directly
      Object.entries(ratios.bondRatios).forEach(([bondId, amount]) => {
        if (helperMap[bondId]) {
          helperMap[bondId].principalDistribution = amount;
        }
      });
    } else {
      // Ratios are percentages - apply to available revenue
      Object.entries(ratios.bondRatios).forEach(([bondId, ratio]) => {
        if (helperMap[bondId]) {
          const amount = roundPrincipalDown(revenueAvailableForDistribution * ratio);

          // Apply target constraint if exists
          if (helperMap[bondId].hasTarget) {
            helperMap[bondId].principalDistribution = Math.min(
              amount,
              helperMap[bondId].remainingTargetCapacity
            );
          } else {
            helperMap[bondId].principalDistribution = amount;
          }
        }
      });

      // Handle any leftover amount
      this.handleLeftover(helperMap, revenueAvailableForDistribution);
    }
  }

  /**
   * Handles any leftover amount after distribution
   */
  private handleLeftover(
    helperMap: RevenueDistributionHelperMap,
    revenueAvailableForDistribution: number
  ): void {
    const totalDistributed = Object.values(helperMap)
      .filter((entry) => entry.isActive)
      .reduce((sum, entry) => sum + entry.principalDistribution, 0);

    const leftover = revenueAvailableForDistribution - totalDistributed;

    if (leftover >= DISTRIBUTION_CONFIG.PRINCIPAL_INCREMENT) {
      // Give to bond with highest capacity (or first unlimited bond)
      const eligibleBonds = Object.entries(helperMap)
        .filter(
          ([_, entry]) =>
            entry.isActive &&
            (!entry.hasTarget || entry.principalDistribution < entry.remainingTargetCapacity)
        )
        .sort((a, b) => b[1].minimumRevenueCapacity - a[1].minimumRevenueCapacity);

      if (eligibleBonds.length > 0) {
        eligibleBonds[0][1].principalDistribution += DISTRIBUTION_CONFIG.PRINCIPAL_INCREMENT;
      }
    }
  }
}
